import React, { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import IndicationComparisonBlock from '../IndicationComparisonBlock';
import ConfirmationPop from '../ConfirmationPopup';
import {
  Study,
  StudyAttribute,
  IndicationItem,
  IndicationPair,
  IndicationApiForComparison,
  AttributeDefinition
} from '../types';
import styles from './styles';
/* eslint-disable react/require-default-props */
interface CompareVersionsModalProps {
  open: boolean;
  onClose: () => void;
  isLoadingPrevData: boolean;
  indicationPairs: IndicationPair[];
  // eslint-disable-next-line no-unused-vars
  onCopyIndication: (prevItem: IndicationItem) => void;
  // eslint-disable-next-line no-unused-vars
  onCopyStudy: (prevStudy: Study, currentIndicationId: number) => void;
  isCopying: boolean;
  copyingStudyId: string | number | null;
  pendingCopyIndication: IndicationItem | null;
  onConfirmCopyIndication: () => Promise<void>;
  onCancelCopyIndication: () => void;
  // eslint-disable-next-line react/require-default-props
  indicationApi?: IndicationApiForComparison;
  // eslint-disable-next-line react/require-default-props
  onRefreshAfterSave?: () => Promise<void>;
  // eslint-disable-next-line react/require-default-props, no-unused-vars
  onShowSnackbar?: (message: string, isError?: boolean) => void;
  nctIdAttributeId: number | null;
  identifierAttributeId: number | null;
  // eslint-disable-next-line react/require-default-props
  attributeOptions?: AttributeDefinition[];
  readOnly?: boolean;
}

const STUDY_SAVE_CONFIRM_MESSAGE =
  'If this study was copied to next set of submissions, please visit them too. Do you want to save?';

type StudyEditState = {
  indicationId: number;
  studyId: number | string;
  studyData: Study;
  isSavePending?: boolean;
} | null;

const CompareVersionsModal = ({
  open,
  onClose,
  isLoadingPrevData,
  indicationPairs,
  onCopyIndication,
  onCopyStudy,
  isCopying,
  copyingStudyId,
  pendingCopyIndication,
  onConfirmCopyIndication,
  onCancelCopyIndication,
  indicationApi,
  onRefreshAfterSave,
  onShowSnackbar,
  nctIdAttributeId,
  identifierAttributeId,
  attributeOptions = [],
  readOnly = false
}: CompareVersionsModalProps) => {
  const [studyEdit, setStudyEdit] = useState<StudyEditState>(null);

  useEffect(() => {
    if (!open) setStudyEdit(null);
  }, [open]);

  const handleSaveStudyClick = useCallback(() => {
    if (readOnly) return;
    if (!studyEdit) return;
    setStudyEdit(prev => (prev ? { ...prev, isSavePending: true } : null));
  }, [readOnly, studyEdit]);

  const handleConfirmStudySave = async () => {
    if (readOnly) return;
    if (!studyEdit || !indicationApi) return;
    try {
      await indicationApi.saveStudyData(studyEdit.indicationId, studyEdit.studyData);
      await onRefreshAfterSave?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save study data';
      onShowSnackbar?.(message, true);
      // eslint-disable-next-line no-console
      console.error('Failed to save study data:', err);
    } finally {
      setStudyEdit(null);
    }
  };

  const handleEditStudy = useCallback(
    (indicationId: number, study: Study) =>
      setStudyEdit({
        indicationId,
        studyId: study.id,
        studyData: {
          ...study,
          attributes: study.attributes?.map((a: StudyAttribute) => ({ ...a })) ?? []
        }
      }),
    []
  );

  const handleUpdateStudyField = useCallback(
    (indicationId: number, key: string, value: string | number | boolean) =>
      setStudyEdit(prev =>
        prev && prev.indicationId === indicationId
          ? { ...prev, studyData: { ...prev.studyData, [key]: value } }
          : prev
      ),
    []
  );

  const handleUpdateStudyAttribute = useCallback(
    (
      indicationId: number,
      attrName: string,
      attrId: number,
      value: string,
      field: 'value' | 'description' = 'value',
      occurrenceIndex = 0
    ) =>
      setStudyEdit(prev => {
        if (!prev || prev.indicationId !== indicationId) return prev;
        const existingAttrs = [...(prev.studyData.attributes ?? [])];
        const matchingIndexes: number[] = [];
        existingAttrs.forEach((a: StudyAttribute, index: number) => {
          if (a.attribute_name === attrName || Number(a.attribute_id) === Number(attrId)) {
            matchingIndexes.push(index);
          }
        });

        const targetIndex = matchingIndexes[occurrenceIndex];
        if (targetIndex != null) {
          const current = existingAttrs[targetIndex];
          existingAttrs[targetIndex] = {
            ...current,
            attribute_name: attrName,
            ...(field === 'description'
              ? { attribute_description: value }
              : { attribute_value: value })
          };
        } else {
          // Pad missing duplicate occurrences so the Nth primary endpoint can be edited.
          while (matchingIndexes.length < occurrenceIndex) {
            existingAttrs.push({
              attribute_id: attrId,
              attribute_name: attrName,
              attribute_value: null,
              attribute_description: null
            });
            matchingIndexes.push(existingAttrs.length - 1);
          }
          existingAttrs.push({
            attribute_id: attrId,
            attribute_name: attrName,
            attribute_value: field === 'value' ? value : null,
            attribute_description: field === 'description' ? value : null
          });
        }

        return {
          ...prev,
          studyData: { ...prev.studyData, attributes: existingAttrs }
        };
      }),
    []
  );

  const handleSaveIndicationTitle = async (indicationId: number) => {
    if (readOnly) return;
    if (!indicationApi || !onRefreshAfterSave) return;
    await indicationApi.saveIndication(indicationId);
    await onRefreshAfterSave();
  };

  const handleCancelStudySave = useCallback(() => {
    setStudyEdit(prev => (prev ? { ...prev, isSavePending: false } : null));
  }, []);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
        <Box sx={styles.header}>
          <Typography variant='h6'>Comparison View</Typography>
        </Box>
        <Box sx={styles.content}>
          {isLoadingPrevData ? (
            <Box sx={styles.loadingBox}>
              <CircularProgress />
            </Box>
          ) : (
            indicationPairs.map(({ indicationId, prevItem, currItem }, index: number) => (
              <IndicationComparisonBlock
                key={indicationId ?? index}
                prevItem={prevItem}
                currItem={currItem}
                index={index}
                indicationId={indicationId}
                indicationApi={indicationApi}
                nctIdAttributeId={nctIdAttributeId}
                identifierAttributeId={identifierAttributeId}
                attributeOptions={
                  attributeOptions.length > 0 ? attributeOptions : indicationApi?.attributes ?? []
                }
                editingStudy={studyEdit}
                onEditStudy={handleEditStudy}
                onSaveStudy={handleSaveStudyClick}
                onUpdateStudyField={handleUpdateStudyField}
                onUpdateStudyAttribute={handleUpdateStudyAttribute}
                onCopyIndication={onCopyIndication}
                onCopyStudy={onCopyStudy}
                onSaveIndicationTitle={handleSaveIndicationTitle}
                isCopying={isCopying}
                copyingStudyId={copyingStudyId}
                readOnly={readOnly}
              />
            ))
          )}
        </Box>
        <Box sx={styles.footer}>
          <Button onClick={onClose} variant='contained'>
            Close
          </Button>
        </Box>
      </Dialog>
      {!readOnly && pendingCopyIndication != null && (
        <ConfirmationPop
          title='Copy indication?'
          message='Are you sure you want to copy data from the previous submission to the current one?'
          onClose={onCancelCopyIndication}
          onConfirm={onConfirmCopyIndication}
        />
      )}
      {studyEdit?.isSavePending && (
        <ConfirmationPop
          title='Save study data'
          message={STUDY_SAVE_CONFIRM_MESSAGE}
          onClose={handleCancelStudySave}
          onConfirm={handleConfirmStudySave}
        />
      )}
    </>
  );
};

export default CompareVersionsModal;
